import * as React from "react";
import { AnalyticsTrigger } from "../../helpers/analytics/analytics-trigger/analytics-trigger.mjs";
import { PropsChecker } from "../../helpers/props-checker/props-checker.mjs";
import { TabsItem } from "../tabs-item/tabs-item.mjs";
import { TabsNav } from "./tabs-nav.mjs";
import { useHeightAnimation } from "./use-height-animation.mjs";
const TabsContext = React.createContext({
  currentTab: "",
  setCurrentTab: (
    /* istanbul ignore next */
    () => null
  ),
  renderHiddenItems: false,
  iconPosition: "inline",
  headingLevel: 2
});
const Tabs = (props) => {
  const getContent = () => {
    if (props.disableAnalyticsTrigger) {
      return /* @__PURE__ */ React.createElement(TabsInner, { ...props });
    }
    return /* @__PURE__ */ React.createElement(
      AnalyticsTrigger,
      {
        trigger: {
          callback: "onTabChange",
          id: ([, nextTab]) => "Tabs - ".concat(nextTab),
          elementId: ([, nextTab]) => "".concat(nextTab),
          elementType: "tab",
          interactionType: "open"
        }
      },
      /* @__PURE__ */ React.createElement(TabsInner, { ...props })
    );
  };
  return /* @__PURE__ */ React.createElement(PropsChecker, { componentProps: props, componentName: "Tabs", defaultValueName: "defaultCurrentTab", valueName: "currentTab", onChangeNames: "onTabChange" }, getContent());
};
const TabsInner = (props) => {
  const {
    align,
    "aria-labelledby": ariaLabelledby,
    border,
    children,
    currentTab,
    defaultCurrentTab,
    headingLevel,
    iconPosition,
    itemComponent,
    onTabChange,
    renderHiddenItems,
    size = "medium",
    className,
    background,
    navigationType,
    fullWidth,
    pullTop,
    ...rest
  } = props;
  const [innerCurrentTab, setInnerCurrentTab] = React.useState(defaultCurrentTab != null ? defaultCurrentTab : "");
  const contentRef = React.useRef(null);
  const contentInnerRef = React.useRef(null);
  const BEM = () => {
    const classArray = ["tabs"];
    if (size) {
      classArray.push("tabs--" + size);
    }
    if (background && size === "huge") {
      classArray.push("tabs--background-" + background);
    }
    if (navigationType) {
      classArray.push("tabs--" + navigationType);
    }
    if (fullWidth) {
      classArray.push("tabs--full-width");
    }
    if (pullTop) {
      classArray.push("tabs--pull-top");
    }
    if (className) {
      classArray.push(className);
    }
    return classArray.join(" ");
  };
  const isCurrentTabControlled = React.useCallback(
    (tab = currentTab) => {
      return !!onTabChange && typeof tab !== "undefined";
    },
    [onTabChange, currentTab]
  );
  const thisCurrentTab = isCurrentTabControlled(currentTab) ? currentTab : innerCurrentTab;
  const { handleTransitionEnd, height } = useHeightAnimation({ currentTab: thisCurrentTab, contentRef, contentInnerRef });
  const setCurrentTab = React.useCallback(
    (id) => {
      if (id === thisCurrentTab) {
        return;
      }
      if (!isCurrentTabControlled()) {
        setInnerCurrentTab(id);
      }
      if (onTabChange) {
        onTabChange(thisCurrentTab, id);
      }
    },
    [thisCurrentTab, isCurrentTabControlled, onTabChange]
  );
  const getNavItems = () => {
    return React.Children.toArray(children).filter((child) => {
      return React.isValidElement(child) && (child.type === TabsItem || child.type === itemComponent);
    }).map((item) => {
      const { children: children2, label, id, ...rest2 } = item.props;
      return {
        ...rest2,
        id: "".concat(id, "-nav-item"),
        current: thisCurrentTab === id,
        onClick: () => setCurrentTab(id),
        text: label,
        url: "#".concat(id),
        "aria-controls": id
      };
    });
  };
  const contextValue = React.useMemo(
    () => ({
      currentTab: thisCurrentTab,
      setCurrentTab,
      renderHiddenItems,
      iconPosition,
      headingLevel: headingLevel != null ? headingLevel : 2,
      background,
      navigationType,
      size,
      align,
      border,
      fullWidth
    }),
    [thisCurrentTab, setCurrentTab, renderHiddenItems, iconPosition, headingLevel, background, navigationType, size, align, border, fullWidth]
  );
  return /* @__PURE__ */ React.createElement(TabsContext.Provider, { value: contextValue }, /* @__PURE__ */ React.createElement("div", { ...rest, className: BEM(), "data-theme": props.theme }, /* @__PURE__ */ React.createElement(TabsNav, { items: getNavItems(), "aria-labelledby": ariaLabelledby }), /* @__PURE__ */ React.createElement("div", { className: "tabs__content-wrapper" }, /* @__PURE__ */ React.createElement("div", { className: "tabs__content", ref: contentRef, style: height === "auto" ? void 0 : { height }, onTransitionEnd: handleTransitionEnd }, /* @__PURE__ */ React.createElement("div", { className: "tabs__content-inner", ref: contentInnerRef }, props.children)))));
};
export {
  Tabs,
  TabsContext
};
